import RpmCcmNavigationHeader from 'components/organisms/rpm-ccm-navigation-header';
import MiniNav from 'components/molecules/mini-nav';
import MobileMiniNav from 'components/molecules/mini-nav/mobile-mini-nav';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityStatus,
  ModuleType,
  ReadingStatus,
} from 'infrastructure/enums';
import {
  useActivityFilter,
  useLinkReadingsToCall,
  useRpmPatientDetails,
} from 'infrastructure/hooks/rpm';
import { useAppDispatch, useAppSelector } from 'store';
import useUser from 'utils/useUser';
import CcmRpmPatientBanner from 'components/molecules/patientTopBanner/ccm-rpm-banner';
import AdminPatientKeyNotes from 'components/organisms/admin-panel-key-notes';
import RpmCcmInteractiveTime from 'components/organisms/interactiveTime/rpm-ccm-interaction-time';
import PatientControlPanel from 'components/organisms/patient-control-panel';
import SinglePatientActivity from 'components/organisms/patient-activity/single-patient-activity';
import PatientAverages from 'components/organisms/patient-averages';
import PastMedicalHistory from 'components/organisms/past-medical-history';
import MedicalHistoryCombineIcon from 'components/atoms/icons/rpm-ccm-medical-history.svg?react';
import CombinedClinicalActivityAndInterventions from 'components/organisms/combined-clinical-activity-and-interventions';
import PatientMedication from 'components/organisms/patient-medication';
import Services from 'components/organisms/rpm-ccm-servises';
import AdminPanelPatientDetailTabs from 'components/organisms/admin-panel/patient-detail/tabbed-forms';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import CombinedGeneralComments from 'components/molecules/general-comments/combined';
import { Routes } from 'router/routes';
import ConditionGoals from 'components/molecules/conditions-goals';
import { useWebsocketRoom } from 'infrastructure/hooks/websockets/use-room';
import WeatherWidget from 'components/organisms/weather-widget';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { isValidGuid } from 'infrastructure/functions';
import { useReadingsCount } from 'infrastructure/hooks/rpm/activity/use-get-readings-count';
import PatientSnapshot from 'components/organisms/patient-snapshot';
import { useGetPatientAssignData } from 'infrastructure/hooks/rpm/assign-tool/use-get-patient-assing';
import {
  addAssigneePatientConfirmShowed,
  isAssigneePatientConfirmShowed,
} from 'infrastructure/functions/assigneePatientConfirmShowed';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useAssignPatient } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import { RunBook } from 'components/organisms/runbooks';
import CallTimer from 'components/molecules/call-timer';
import { useCallEndedEventListener } from 'infrastructure/customEvents/callEvents';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';

import s from './styles.module.scss';

import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';

const calculateUnreviewedReadings = (
  activityCount: IRpmActivityCountResponse | undefined,
) => {
  if (!activityCount) return -1; // as default. Need for not show the alert if activityCount is undefined

  return activityCount.critical + activityCount.normal + activityCount.risk;
};

const UnifiedPatientProfile = () => {
  const { t } = useTranslation();
  const { patientGuid } = useParams();
  if (!patientGuid || !isValidGuid(patientGuid)) {
    return null;
  }

  const {
    isMobileDevice,
    isAdmin,
    isAgencyDoctor,
    isPatient,
    isMedicalAssistantRole,
    loginGuid,
    isFullyManagedAgency,
  } = useUser();
  const { filter } = useActivityFilter();
  const [startTime, endTime] = useAppSelector<[number, number]>(
    (state) => state.patient.dates,
  );

  const { patientAssignData } = useGetPatientAssignData(patientGuid);
  const { assignPatient } = useAssignPatient();
  const { activityCount, refetchCount } = useReadingsCount({
    ...filter,
    reviewed: false,
    patientGuid,
    page: 1,
    items: 1,
    status: ReadingStatus.Critical,
    startTime,
    endTime,
    skipMACheck: isMedicalAssistantRole,
  });
  const { Alert, showAlert } = useAlert();

  useEffect(() => {
    if (
      isMedicalAssistantRole &&
      activityCount?.critical &&
      activityCount?.critical > 0
    ) {
      notificationController.error({
        message: t('errors.patientHasUnactionedCriticalReadings'),
        closable: true,
        duration: 0,
      });
    }
  }, [isMedicalAssistantRole, activityCount?.critical]);

  const unactionedReadingsCount = calculateUnreviewedReadings(activityCount);

  useEffect(() => {
    if (
      unactionedReadingsCount === 0 &&
      loginGuid === patientAssignData?.assignee?.loginGuid &&
      !isAssigneePatientConfirmShowed(patientGuid)
    ) {
      showAlert({
        title: t('labels.unassign'),
        message: t('messages.unassigngConfirmMessage'),
        messageTitle: t('messages.unassigngConfirmTitle'),
        type: 'primary',
        successBtnLabel: t('controls.yes'),
        rejectBtnLabel: t('controls.no'),
      }).then((answer) => {
        if (answer) {
          addAssigneePatientConfirmShowed(patientGuid);
          assignPatient({
            patientGuid,
            action: 'unassign',
          });
        }
      });
    }
  }, [
    unactionedReadingsCount,
    patientAssignData?.assignee?.loginGuid,
    loginGuid,
  ]);

  const {
    query: { patient, isLoading },
    clinicGuid,
  } = useRpmPatientDetails({ patientGuid });

  useWebsocketRoom('rpmPatientProfile', { patientGuid });

  const { isReadOnlyRole } = useCheckRoleAccess();

  const { patientInfo } = usePatientInfo({
    patientGuid,
  });
  const { readingIds } = useAppSelector((state) => state.linkedCallsReadings);
  const { resetState } = useAppDispatch().linkedCallsReadings;
  const { linkReadingsToCall } = useLinkReadingsToCall();
  const { updateInteractionStatus } = useUpdateInteractionStatus();
  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({ patientGuid });

  useCallEndedEventListener(async (event) => {
    const MIN_SUCCESSFUL_CALL_DURATION = 30;
    const details = event.detail;

    const interactionStatus = patientInfo?.interactionStatus;
    if (
      interactionStatus === false &&
      details.callDuration >= MIN_SUCCESSFUL_CALL_DURATION &&
      details.patientGuid === patientGuid
    ) {
      const result = await showMonthlyInteractionAlert({
        interactionType: 'call',
      });
      if (result) {
        await updateInteractionStatus({
          patientGuid,
          isInteracted: true,
        });
      }
    }

    if (
      details.module === ModuleType.RPM &&
      readingIds.length > 0 &&
      details.callDuration >= MIN_SUCCESSFUL_CALL_DURATION &&
      details.patientGuid === patientGuid
    ) {
      linkReadingsToCall(
        {
          callId: details.callId,
          readingIds,
          patientGuid,
        },
        {
          onSettled: () => {
            resetState();
          },
        },
      );
    }
  });

  return (
    <div className={s.wrapper}>
      <CallTimer patientGuid={patientGuid} />
      {MonthlyInteractionAlert}
      <RpmCcmNavigationHeader
        patientGuid={patientGuid}
        clinicGuid={clinicGuid}
        module={ModuleType.RPMCCM}
        goBackPath={Routes.overviewDashboard}
        title={t('labels.patientProfileTitle')}
      />
      {!isMobileDevice && (isAdmin || isAgencyDoctor) && (
        <AdminPatientKeyNotes patientGuid={patientGuid} opened />
      )}
      {!isMobileDevice && clinicGuid && (isAdmin || isAgencyDoctor) && (
        <RunBook clinicGuid={clinicGuid} patientGuid={patientGuid} />
      )}
      <WeatherWidget
        city={patient?.profile?.city}
        state={patient?.profile?.state}
        timezone={patient?.profile?.timezone}
      />
      <MiniNav module={ModuleType.RPMCCM} activityCount={activityCount} />
      <MobileMiniNav module={ModuleType.RPMCCM} activityCount={activityCount} />
      <div className={s.body}>
        <div className={s['control-banner-time-wrapper']}>
          {!isPatient && (
            <CcmRpmPatientBanner
              patientGuid={patientGuid}
              careAgents={patient?.careAgents ?? []}
              patientDetailsLoading={isLoading}
            />
          )}
          <PatientSnapshot patientGuid={patientGuid} />
          <RpmCcmInteractiveTime patientGuid={patientGuid} />
          <PatientControlPanel
            patientGuid={patientGuid}
            module={ModuleType.RPMCCM}
            clinicGuid={clinicGuid}
          />
        </div>
        {isFullyManagedAgency && isAgencyDoctor && (
          <>
            <SinglePatientActivity
              patientGuid={patientGuid}
              type={ActivityStatus.UnactionedCritical}
              tableId="unactionedCritical-activity"
              onReadingsRefetch={refetchCount}
            />
            <SinglePatientActivity
              patientGuid={patientGuid}
              type={ActivityStatus.UnactionedNormalRisk}
              tableId="unactionedNormalRisk-activity"
              onReadingsRefetch={refetchCount}
            />
          </>
        )}
        {(!isAgencyDoctor || !isFullyManagedAgency) && (
          <SinglePatientActivity
            patientGuid={patientGuid}
            type={ActivityStatus.Unactioned}
            tableId="unactioned-activity"
            onReadingsRefetch={refetchCount}
          />
        )}
        <SinglePatientActivity
          patientGuid={patientGuid}
          type={ActivityStatus.Actioned}
          tableId="actioned-activity"
        />
        <PatientAverages patientGuid={patientGuid} endTime={endTime} />
        <PastMedicalHistory
          patientGuid={patientGuid}
          icon={<MedicalHistoryCombineIcon width={28} height={28} />}
        />
        <CombinedClinicalActivityAndInterventions patientGuid={patientGuid} />
        <PatientMedication
          patientGuid={patientGuid}
          module={ModuleType.RPMCCM}
        />
        <Services
          patientGuid={patientGuid}
          thresholds={patient?.thresholds}
          patientDetailsLoading={isLoading}
        />
        <ConditionGoals
          patientGuid={patientGuid}
          disableActions={isReadOnlyRole}
        />
        <div id="user-profile">
          <AdminPanelPatientDetailTabs
            patientGuid={patientGuid}
            module={ModuleType.RPMCCM}
            readOnly={isReadOnlyRole}
          />
        </div>
        <CombinedGeneralComments
          patientGuid={patientGuid}
          interactionStatus={patient?.profile.interactionStatus}
        />
      </div>
      {Alert}
    </div>
  );
};
export default UnifiedPatientProfile;
